import { SELECTED_COMMERCIAL_PRODUCT_INDEX } from '../../common/constants/commonConstants.js';
import { AnalyticsListNames, DEFAULT_ITEM_BRAND } from '../../common/utils/analyticsUtils';
import { isCampaignPresent } from '../../common/utils/campaignUtils.js';
import { Category } from '../../common/utils/cartProductUtils.js';
import { centsToEuros, getMonthlyPrice } from '../../common/utils/priceUtils.js';
import { isInBrowser } from '../../common/utils/ssrUtils.js';
import { VAT_AS_DECIMAL } from '../../common/utils/taxUtils.js';
import { CommercialProductType } from '../../generated/api/models.js';
import { TypeKeys } from '../actions/index.js';
import type { EcommerceItem } from '../../common/analytics.js';
import type { AddedAddon } from '../../common/types/addon.js';
import type { ShoppingCartItemForCheckout } from '../../common/types/checkout.js';
import type { ConfiguredCommercialProduct, ConfiguredOffer } from '../../common/types/commercialProduct.js';
import type { CampaignAssociation, CommercialProduct } from '../../generated/api/models.js';
import type { GoogleEcommerceActionTypes } from '../actions/index.js';
import type { Action } from 'redux';

export enum PaymentTypeEnum {
  INVOICE = 'invoice',
  CREDIT_CARD = 'credit card',
}

const GOOGLE_ENHANCED_ECOMMERCE_ACTIONS = [
  TypeKeys.GOOGLE_ECOMMERCE_ADD_PAYMENT_INFO,
  TypeKeys.GOOGLE_ECOMMERCE_ADD_SHIPPING_INFO,
  TypeKeys.GOOGLE_ECOMMERCE_ADD_TO_CART,
  TypeKeys.GOOGLE_ECOMMERCE_EVENT,
  TypeKeys.GOOGLE_ECOMMERCE_CHECKOUT,
  TypeKeys.GOOGLE_ECOMMERCE_PRODUCT_CLICK,
  TypeKeys.GOOGLE_ECOMMERCE_PRODUCT_DETAIL,
  TypeKeys.GOOGLE_ECOMMERCE_PURCHASE,
  TypeKeys.GOOGLE_ECOMMERCE_REMOVE_FROM_CART,
];

export const isGoogleEcommerceAction = (action: Action): action is GoogleEcommerceActionTypes =>
  isInBrowser() && GOOGLE_ENHANCED_ECOMMERCE_ACTIONS.indexOf(action.type) > -1;

const calculateAddonOneTimeRevenue = (addedAddOns?: AddedAddon[]) => {
  return (addedAddOns || []).reduce((sum: number, addon) => sum + (addon.oneTimePrice ?? 0), 0);
};

const calculateAddonMonthlyRevenue = (addedAddOns?: AddedAddon[]) => {
  return (addedAddOns || []).reduce((sum: number, addon) => sum + (addon.monthlyPrice ?? 0), 0);
};

export const getProductCampaignAssociation = ({ campaignAssociations }: CommercialProduct) => {
  if (campaignAssociations?.[0] && isCampaignPresent(campaignAssociations)) {
    return campaignAssociations[0];
  }
  return undefined;
};

const calculateFirstYearAddOnRevenue = (addOns: AddedAddon[]) => {
  const addOnMonthlies = calculateAddonMonthlyRevenue(addOns);
  const addOnOneTimes = calculateAddonOneTimeRevenue(addOns);

  return addOnOneTimes + 12 * addOnMonthlies;
};

export const calculateProductRevenue = (
  cp: CommercialProduct,
  addedAddOns?: AddedAddon[],
  ca?: CampaignAssociation
) => {
  const pca = ca ?? getProductCampaignAssociation(cp);

  const addOnMonthlies = calculateAddonMonthlyRevenue(addedAddOns);
  const addOnOneTimes = calculateAddonOneTimeRevenue(addedAddOns);

  const monthlyRecurringCharge = cp.monthlyRecurringCharge
    ? getMonthlyPrice(cp.monthlyRecurringCharge - (pca?.monthlyRecurringCharge ?? 0), cp.billingPeriod) || 0
    : 0;

  const oneTimeCharge = cp.oneTimeCharge ? cp.oneTimeCharge - (pca?.oneTimeCharge ?? 0) : 0;

  // Subscriptions' payments are perpetual (0), so we calculate a year's worth
  const monthlyRecurringCharges = (monthlyRecurringCharge + addOnMonthlies) * (cp.payments || 12);

  const oneTimeCharges = oneTimeCharge + addOnOneTimes;

  return monthlyRecurringCharges + oneTimeCharges;
};

type ProductPriceData = {
  totalRevenue: number;
  totalShipping: number;
  totalTax: number;
  eCommerceItem: EcommerceItem[];
};

export const getProductPriceData = (cos: ConfiguredOffer[]): ProductPriceData => {
  const totalShipping = 0;
  let totalRevenue = 0;
  const itemMap = new Map<string, EcommerceItem>();

  cos.forEach(curr => {
    const co = curr.offer;
    const ca = curr.selectedCampaignAssociation;
    curr.selectedCommercialProducts.forEach((ccp: ConfiguredCommercialProduct) => {
      const cp = ccp.commercialProduct;
      const revenue = calculateProductRevenue(ccp.commercialProduct, ccp.addedAddOns, ca);
      /* eslint-disable @typescript-eslint/naming-convention */
      const item_id = curr.onlineModelCode ?? co.offerCode;

      // Use device voucher if present, otherwise use offer voucher, there can be only one at a time
      const voucher = ccp.deviceVoucher?.voucherCode ?? curr?.voucher;

      if (itemMap.has(item_id)) {
        const existing = itemMap.get(item_id)!;
        existing.quantity = (existing.quantity ?? 1) + 1;
      } else {
        itemMap.set(item_id, {
          ...(voucher ? { coupon: voucher } : {}),
          ...(ccp.manufacturer ? { item_brand: ccp.manufacturer } : {}),
          item_category: ccp.category ?? cp.productSubType ?? cp.productType,
          item_id,
          item_name: curr.onlineModelName ?? co.offerName,
          item_variant: cp.commercialProductName,
          item_brand: ccp.manufacturer,
          price: centsToEuros(revenue),
          quantity: 1,
        });
      }
      totalRevenue += revenue;
    });
  });

  return {
    totalRevenue,
    totalShipping,
    totalTax: totalRevenue * VAT_AS_DECIMAL,
    eCommerceItem: Array.from(itemMap.values()),
  };
};

const createEcommerceEventProduct = (
  item_id: string,

  item_category: string,

  item_name: string,
  onetimeChargeInCents: number,
  recurringChargeInCents: number,
  recurrences: number,

  item_brand?: string,

  item_variant?: string,
  quantity?: number,

  item_list_name?: string,
  index?: number // position in list
): EcommerceItem => {
  // This is the price _per item_, not the total.
  const price = centsToEuros(onetimeChargeInCents + recurringChargeInCents * recurrences);
  return {
    ...(index ? { index } : {}),
    item_category,
    ...(item_brand ? { item_brand } : {}),
    item_id,
    ...(item_list_name ? { item_list_name } : {}),
    item_name,
    item_variant,
    price,
    quantity,
  };
};

export const createEcommerceItemFromCommercialProducts = (
  id: string,
  category: string | undefined,
  name: string,
  variant: string,
  commercialProducts: CommercialProduct[],
  quantity: number,
  brand?: string
): EcommerceItem => {
  return createEcommerceEventProduct(
    id,
    category ?? commercialProducts[0].productSubType ?? commercialProducts[0].productType,
    name,
    commercialProducts.reduce((sum, cp) => sum + (cp.oneTimeCharge || 0), 0),
    commercialProducts.reduce(
      (sum, cp) => sum + (getMonthlyPrice(cp.monthlyRecurringCharge, cp.billingPeriod) || 0),
      0
    ),
    // Subscriptions' payments are perpetual (0), so we calculate a year's worth
    commercialProducts[0].payments || 12,
    brand,
    variant,
    quantity
  );
};

const getShoppingCartItemManufacturer = (shoppingCartItem: ShoppingCartItemForCheckout) => {
  const { category, manufacturer, onlineModelName } = shoppingCartItem;
  if (
    (manufacturer == null || manufacturer === 'N/A') &&
    category === Category.SALES_PRODUCT &&
    (onlineModelName.startsWith('Elisa') || onlineModelName.startsWith('Yritysdata 5G'))
  ) {
    return 'Elisa';
  }
  return manufacturer;
};

export const createEcommerceItemFromShoppingCartItem = (
  shoppingCartItem: ShoppingCartItemForCheckout,
  quantity?: number
): EcommerceItem => {
  const manufacturer = getShoppingCartItemManufacturer(shoppingCartItem);
  return createEcommerceEventProduct(
    shoppingCartItem.onlineModelCode ?? shoppingCartItem.offerCode,
    shoppingCartItem.category,
    shoppingCartItem.onlineModelName ?? shoppingCartItem.productName,
    shoppingCartItem.price.onetime?.price ?? 0,
    getMonthlyPrice(shoppingCartItem.price.periodic?.price, shoppingCartItem.price.periodic?.billingPeriod) ?? 0,
    // Subscriptions' payments are perpetual (0), so we calculate a year's worth
    shoppingCartItem.price.periodic?.payments || 12,
    manufacturer,
    shoppingCartItem.productName,
    quantity ?? shoppingCartItem.quantity
  );
};

export const createNettiliteEcommerceItemFromConfiguredProduct = (
  cp: CommercialProduct,
  voucher?: string,
  addedAddOns?: AddedAddon[],
  ca?: CampaignAssociation
): EcommerceItem => ({
  coupon: voucher,

  item_brand: DEFAULT_ITEM_BRAND,

  item_category: cp.productType,

  item_id: cp.commercialProductCode,

  item_list_name: AnalyticsListNames.NETTI_LITE_LIST_NAME,

  item_name: cp.commercialProductName,

  item_variant: cp.commercialProductName,
  price: centsToEuros(calculateProductRevenue(cp, addedAddOns, ca)),
  quantity: 1,
});

export const createNettiliteEcommerceItemFromAddedAddon = (addedAddOn: AddedAddon): EcommerceItem => ({
  item_brand: DEFAULT_ITEM_BRAND,

  item_category: CommercialProductType.FIXED_BROADBAND,

  item_id: addedAddOn.addOnCode,

  item_list_name: AnalyticsListNames.NETTI_LITE_LIST_NAME,

  item_name: addedAddOn.displayName,

  item_variant: addedAddOn?.addOnAttributes?.displayNameOverride ?? addedAddOn.displayName,
  price: centsToEuros(calculateFirstYearAddOnRevenue([addedAddOn])),
  quantity: 1,
});

export const createNettiliteEcommerceItemsFromConfiguredOffer = (configuredOffer: ConfiguredOffer) => {
  const eCommerceEventProducts = configuredOffer.selectedCommercialProducts.map(scp =>
    createNettiliteEcommerceItemFromConfiguredProduct(scp.commercialProduct, configuredOffer.voucher)
  );

  const addedAddOns = configuredOffer.selectedCommercialProducts[SELECTED_COMMERCIAL_PRODUCT_INDEX].addedAddOns || [];

  // Addon quantity may be larger than one, in such cases duplicates should be
  // removed but the first year revenue should still be calculated correctly.
  const uniqueAddOns = addedAddOns.reduce(
    (acc: { [p: string]: AddedAddon }, addOn: AddedAddon): { [p: string]: AddedAddon } => {
      if (acc[addOn.addOnCode]) {
        if (acc[addOn.addOnCode].monthlyPrice) {
          acc[addOn.addOnCode].monthlyPrice = (acc[addOn.addOnCode].monthlyPrice ?? 0) + (addOn?.monthlyPrice ?? 0);
        }
      } else {
        acc[addOn.addOnCode] = JSON.parse(JSON.stringify(addOn)); // Copy to avoid mutating original's monthlyPrice
        acc[addOn.addOnCode].displayName =
          acc[addOn.addOnCode]?.addOnAttributes?.displayNameOverride ?? acc[addOn.addOnCode].displayName;
      }
      return acc;
    },
    {}
  );

  return [
    ...eCommerceEventProducts,
    ...Object.values(uniqueAddOns).map(addedAddon => createNettiliteEcommerceItemFromAddedAddon(addedAddon)),
  ];
};
